import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/element/center-under-pointer'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { useEffect, type ReactNode, type RefObject } from 'react'
import { render, unmountComponentAtNode } from 'react-dom'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { type CalendarItem } from '../types'
import { toDragData } from './dragData'
import { DragGhost } from './DragGhost'
import { useCalendarDragState } from './useCalendarDragState'

/**
 * Lets a chip or a bar be picked up and dropped on another day.
 *
 * What travels under the pointer is the ghost rather than the element itself,
 * so every item is dragged by the same small shape. The preview is drawn into a
 * container of the browser's own, outside the app's tree, which is why it
 * carries the theme in with it.
 *
 * Nothing happens when `isEnabled` is false, which is how a calendar with no
 * reschedule handler — a story, a read-only mount — stays a still picture.
 */
export const useDraggableCalendarItem = ({
  color,
  isEnabled,
  item,
  parent,
  ref,
  renderChannelIcon,
}: {
  /** The color the item is drawn in, so the ghost carries the same one. */
  color?: string
  isEnabled: boolean
  item: CalendarItem
  parent?: CalendarItem
  ref: RefObject<HTMLElement>
  renderChannelIcon?: (item: CalendarItem) => ReactNode
}) => {
  const onDragStart = useCalendarDragState((state) => state.onDragStart)
  const reset = useCalendarDragState((state) => state.reset)
  const theme = useTheme()

  useEffect(() => {
    const element = ref.current
    if (!element || !isEnabled) return

    return draggable({
      element,
      getInitialData: () => toDragData({ item, parent }),
      onGenerateDragPreview: ({ nativeSetDragImage }) =>
        setCustomNativeDragPreview({
          getOffset: centerUnderPointer,
          nativeSetDragImage,
          render: ({ container }) => {
            render(
              <ThemeProvider theme={theme}>
                <DragGhost
                  color={color}
                  item={item}
                  renderChannelIcon={renderChannelIcon}
                />
              </ThemeProvider>,
              container
            )
            return () => unmountComponentAtNode(container)
          },
        }),
      onDragStart: () => onDragStart(item.id),
      onDrop: () => reset(),
    })
  }, [
    color,
    isEnabled,
    item,
    onDragStart,
    parent,
    ref,
    renderChannelIcon,
    reset,
    theme,
  ])
}
